import React, {useState} from "react";
import styled from 'styled-components';
import InputMask, { BeforeMaskedStateChangeStates } from "react-input-mask";
import { FieldWrapper, Label, Border, ErrorMessage } from './TextField';

const StyledCurrencyInput = styled(InputMask)`
  display: block;
  font-size: 16px;
  width: 100%;
  padding: 6px 6px 6px 0px;
  color: #d4d4d4;
  outline: none;
  border: none;
  background: transparent;

  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    border: none;
    -webkit-text-fill-color: #d4d4d4;
    -webkit-box-shadow: none;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

interface CurrencyFieldProps {
    name: string;
    label: string;
    min?: number;
    max?: number;
    prefix?: string;
    initialValue?: string;
    onChange: (value: number) => void;
    error: string;
    setError: (name: string, error: string) => void;
}

const CurrencyField: React.FC<CurrencyFieldProps> = ({name = "", label = "", min = 0, max = Infinity, prefix = "$", initialValue = "", onChange = () => {}, error = "", setError = () => {}}) => {

    const [value, setValue] = useState(initialValue);
    const [focus, setFocus] = useState(false);

    const handleFocus = () => {
        setFocus(true);
    };

    const handleBlur = () => {
        setFocus(false);
    };

    const cleanValue = (val: string) => val.replace(new RegExp(`[^0-9.]`, 'g'), '').replace(/(\..*?)\..*/g, '$1');

    const limitDecimals = (val: string) => {
        if(!val) return "";
        const [intPart, decimalPart] = val.split('.');
        return decimalPart !== undefined ? `${intPart}.${decimalPart.slice(0,2)}` : intPart;
    }

    const formatValue = (val: string) => {
        if (!val || val === '') return '';
        const [intPart, decimalPart] = val.toString().split('.');
        const formattedInt = parseInt(intPart || '0', 10).toLocaleString('en-US').replace(/,/g, ' ');
        return prefix + ' ' + formattedInt + (decimalPart !== undefined ? '.' + decimalPart : '');
    };

    const beforeMaskedValueChange = (newState: BeforeMaskedStateChangeStates) => {
        const clean = cleanValue(newState.value);
        const formatted = formatValue(clean);
        return {
            value: formatted,
            selection: { start: formatted.length, end: formatted.length },
        };
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const clean = limitDecimals(cleanValue(e.target.value)); 
        const parsed = parseFloat(clean);
        const isValid = /^\d+(\.\d{2})?$/.test(clean);
        if(clean && !isValid) {
            setError(name, "Invalid value");
        }
        else {
            setError(name, "")
        }

        if (clean === '' || isNaN(parsed)) {
            setValue('');
            onChange?.(0);
            return;
        }

        if (parsed >= min && parsed <= max) {
            setValue(clean);
            onChange?.(parsed);
        }
    };

    return (
        <FieldWrapper>
            <Label htmlFor="credit-card" active={focus || Boolean(error)}>{label}</Label>
            <StyledCurrencyInput
                id="credit-card"
                type="tel"      
                name={name}
                placeholder="$ 9.99"
                mask=""
                value={formatValue(value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                beforeMaskedValueChange={beforeMaskedValueChange}
            />
            <Border className="divider" isFocused={focus} />
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </FieldWrapper>
    )
}

export default React.memo(CurrencyField);